import React, {useEffect} from "react";
import {Pane, Label, TextInput, Select} from "evergreen-ui";
import {useForm, Controller, FormProvider} from "react-hook-form";
import {differenceInBusinessDaysLocal, parseBackendDateISO, toBackendDateISO} from "common/util/date";
import {format} from "date-fns";
import {useUpdateReferralOffer} from "./useUpdateReferralOffer";
import {ReportModal} from "../../form-components/ReportModal";
import {FatOutlineButton} from "common/component/FatOutlineButton";
import {OfferStatus} from "../../../../offer/domain/types";
import {ModalDatePicker} from "../../form-components/ModalDatePicker";
import {showToast} from "common";
import {serviceTypeMapping} from "referral";
import {ServiceLocationEnumNameToValue} from "placement/const";
import {
    caregiverLocationDisplay,
    offerCaregiverLocations
} from "offer/domain/offer";
import {uniq} from "lodash";
import { ReferralOffer } from "../domain";
import {InlineWarningPanel} from "./InlineWarningPanel";

const NO_AUTH_ERROR_MESSAGE = 'The request cannot be submitted at this time as there is no authorization associated with this referral. Once the authorization has been processed, the initial offer date may be updated';

// A Treatment-Only caregiver offer stores no assessment location, so fall back to the
// per-level ones. This screen keeps ServiceLocationEnumNameToValue rather than switching to
// ServiceLocationTypeMap, because the two maps disagree on School/Day care.
const serviceLocationDisplay = (offer: ReferralOffer): string => {
    const locationCond = caregiverLocationDisplay(
        offerCaregiverLocations(offer),
        offer.treatmentOffered);

    const keys = locationCond.showAssessment || !(locationCond.showMidLevel || locationCond.showHighLevel)
        ? [offer.serviceLocationType]
        : uniq([offer.serviceLocationMidLevelType, offer.serviceLocationHighLevelType]);

    return keys.filter(Boolean)
        .map(key => ServiceLocationEnumNameToValue.get(key) ?? key)
        .join('; ');
};

type FormValues = {
    servicesProvided: string; // "--None--" | "Yes" | "No"
    updatedFirstServiceDate: string;
};

type Props = {
    clientGuid: string;
    offer: ReferralOffer | null;
    visible: boolean;
    onClose: () => void;
    onRequireStaffingPlan?: (offer: ReferralOffer) => void;
    onRequireClinicalConsult?: (offer: ReferralOffer) => void;
};

export function ReferralOfferModal({clientGuid, offer, visible, onClose, onRequireStaffingPlan, onRequireClinicalConsult}: Props) {

    const methods = useForm<FormValues>({
        defaultValues: {
            servicesProvided: "--None--",
            updatedFirstServiceDate: ""
        }
    });

    const { control, handleSubmit, watch, reset } = methods;

    const servicesProvided = watch("servicesProvided");
    const updatedDate = watch("updatedFirstServiceDate");

    const {updateReferralOffer, loading, error} = useUpdateReferralOffer();

    // Force "Yes" when Portal/SF signals billing has begun — cannot be overridden by the user.
    const hasBilledHours = offer?.authorization?.hasBilledHours === true;

    useEffect(() => {
        if (visible && offer) {
            reset({
                servicesProvided: hasBilledHours ? "Yes" : "--None--",
                updatedFirstServiceDate: ""
            });
        }
    }, [visible, offer, reset, hasBilledHours]);

    // Navigation gate: the downstream Staffing Plan / Consult submission is anchored to an
    // authorization end-to-end (Portal sidecar keyed by authorizationGuid; SF Case filed under
    // Authorization__c). So an authorization must exist — but its status is irrelevant. [BHPNPP-3043]
    const hasAuthorization = offer?.authorization != null;

    const handleTransitionToStaffingPlan = () => {
        if (!hasAuthorization) {
            showToast.error(NO_AUTH_ERROR_MESSAGE);
            return;
        }
        onRequireStaffingPlan?.(offer!);
    };

    const handleTransitionToClinicalConsult = () => {
        if (!hasAuthorization) {
            showToast.error(NO_AUTH_ERROR_MESSAGE);
            return;
        }
        onRequireClinicalConsult?.(offer!);
    };

    // Derived validations for rendering reactive feedback (Scenario 1 Strict Rule)
    // Block if offer is VOID (historical) or already a clone (one update per original)
    const isAlreadyUpdated = !!offer?.originalOfferGuid || offer?.status === OfferStatus.VOID;
    const isStaffingPlanRequired = servicesProvided === "Yes";

    let chronologicalValidationError = '';
    let isClinicalConsultRequired = false;

    // Parse the backend (SF LA tz) ISO date directly to a local Date object, ignoring timezone offsets to avoid drift
    const parsedFirstServiceDate = offer?.firstServiceDate ? parseBackendDateISO(offer.firstServiceDate) : null;

    if (updatedDate && parsedFirstServiceDate) {
        // Compare calendar dates (yyyy-MM-dd) to avoid timezone/time-of-day edge cases
        const origDateStr = format(parsedFirstServiceDate, "yyyy-MM-dd");
        const updatedDateStr = format(new Date(updatedDate), "yyyy-MM-dd");

        if (updatedDateStr < origDateStr) {
            chronologicalValidationError = 'The new first offered date value cannot be before the original date.';
        } else if (updatedDateStr === origDateStr) {
            chronologicalValidationError = 'The new first offered date value cannot be the same as the original date.';
        } else {
            const diffBizDays = differenceInBusinessDaysLocal(updatedDateStr, origDateStr);
            if (diffBizDays > 20) {
                isClinicalConsultRequired = true;
            }
        }
    }

    // --- Inline warning content (single slot, priority order) ---
    let warningContent: React.ReactNode = null;
    const linkButtonClass = "underline text-blue-600 font-semibold bg-transparent p-0 border-0 cursor-pointer";
    if (isAlreadyUpdated) {
        warningContent = (
            <span>
                The new first offered date was already updated once before and cannot be updated a second time. Please submit a{' '}
                <button type="button" className={linkButtonClass} onClick={handleTransitionToClinicalConsult}>
                    consult
                </button>{' '}with Catalight Care Services clinical team
            </span>
        );
    } else if (isStaffingPlanRequired) {
        warningContent = (
            <span>
                The First Date of Offer cannot be updated because billing has already begun for this client. To request a change, please submit a &nbsp;
                <button type="button" className={linkButtonClass} onClick={handleTransitionToStaffingPlan}>
                    Staffing Plan
                </button>.
            </span>
        );
    } else if (isClinicalConsultRequired) {
        warningContent = (
            <span>
                The new first offered date can only be up to 20 business days from the original date. Please submit a{' '}
                <button type="button" className={linkButtonClass} onClick={handleTransitionToClinicalConsult}>
                    consult
                </button>{' '}with Catalight Care Services clinical team
            </span>
        );
    }

    const isBlocked = isAlreadyUpdated || isStaffingPlanRequired || (chronologicalValidationError !== '') || isClinicalConsultRequired || servicesProvided === "--None--" || (servicesProvided === "No" && !updatedDate);

    const onSubmit = (data: FormValues) => {
        if (isBlocked) return;

        showToast.notify("Please wait while we process the update");

        // Convert the updated local date back to LA midnight timezone for backend consistency
        const finalDateIso = toBackendDateISO(data.updatedFirstServiceDate ? new Date(data.updatedFirstServiceDate) : null);

        updateReferralOffer({
            variables: {
                input: {
                    originalOfferGuid: offer?.guid,
                    updatedFirstServiceDate: finalDateIso
                }
            }
        }).then(() => {
            showToast.success("The offer is updated successfully");
            onClose();
        }).catch(e => {
            console.error(e);
        });
    };

    if (!offer) return null;

    return (
        <ReportModal
            visible={visible}
            title="Referral Offer"
            onClose={onClose}
            maxWidth="max-w-3xl"
        >
            <FormProvider {...methods}>
            <form onSubmit={handleSubmit(onSubmit)}>
                <div className="flex flex-col w-full p-10 overflow-y-auto" style={{ maxHeight: "calc(100vh - 200px)" }}>
                    {error && (
                        <div className="bg-red-50 text-red-800 p-3 mb-8 rounded border border-red-200 text-sm">
                            {error.message.includes(' : ') ? error.message.split(' : ').pop() : error.message}
                        </div>
                    )}

                    <div className="flex flex-col space-y-4">
                        <Pane display="flex" marginBottom={16} alignItems="flex-start">
                            <Pane width={240} display="flex" justifyContent="flex-end" paddingRight={24} paddingTop={8}>
                                <Label>Service Type</Label>
                            </Pane>
                            <Pane flex={1}>
                                <TextInput value={(offer.referral?.serviceTypes || []).map(st => serviceTypeMapping[st] ?? st).join(', ')} disabled width="100%" />
                            </Pane>
                        </Pane>

                        <Pane display="flex" marginBottom={16} alignItems="flex-start">
                            <Pane width={240} display="flex" justifyContent="flex-end" paddingRight={24} paddingTop={8}>
                                <Label>Service Location</Label>
                            </Pane>
                            <Pane flex={1}>
                                <TextInput value={serviceLocationDisplay(offer)} disabled width="100%" />
                            </Pane>
                        </Pane>

                        <Pane display="flex" marginBottom={16} alignItems="flex-start">
                            <Pane width={240} display="flex" justifyContent="flex-end" paddingRight={24} paddingTop={8}>
                                <Label>First Services Date Offered</Label>
                            </Pane>
                            <Pane flex={1}>
                                <TextInput value={parsedFirstServiceDate ? format(parsedFirstServiceDate, "MM/dd/yyyy") : ""} disabled width="100%" />
                                <div className="text-gray-500 text-xs italic mt-2">
                                    Pre-populated from Referral Offer record
                                </div>
                            </Pane>
                        </Pane>

                        <Controller
                            name="servicesProvided"
                            control={control}
                            render={({field}) => (
                                <Pane display="flex" marginBottom={16} alignItems="flex-start">
                                    <Pane width={240} display="flex" justifyContent="flex-end" paddingRight={24} paddingTop={8}>
                                        <Label>Have Services been provided?</Label>
                                    </Pane>
                                    <Pane flex={1}>
                                        <Select {...field} disabled={isAlreadyUpdated || loading || hasBilledHours} width="100%">
                                            <option value="--None--">--None--</option>
                                            <option value="No">No</option>
                                            <option value="Yes">Yes</option>
                                        </Select>
                                    </Pane>
                                </Pane>
                            )}
                        />

                        {servicesProvided === "No" &&
                          <Pane display="flex" marginBottom={16} alignItems="flex-start">
                              <Pane width={240} display="flex" justifyContent="flex-end" paddingRight={24} paddingTop={8}>
                                  <Label>Updated First Service Date</Label>
                              </Pane>
                              <Pane flex={1}>
                                  <div style={{ marginLeft: -16, width: '100%' }}>
                                      <ModalDatePicker<FormValues>
                                          disabled={isStaffingPlanRequired || isAlreadyUpdated || servicesProvided !== "No"}
                                          label={''}
                                          field={'updatedFirstServiceDate'}
                                          required={false}
                                          containerClassName="w-full flex text-base items-center pb-2"
                                          labelClassName="hidden"
                                          datePickerWrapperClassName="w-full"
                                          placeholder="mm/dd/yyyy"
                                      />
                                  </div>
                                  {chronologicalValidationError && (
                                      <div className="text-red-500 text-sm mt-2">
                                          {chronologicalValidationError}
                                      </div>
                                  )}
                              </Pane>
                          </Pane>
                        }

                        {warningContent && (
                            <Pane display="flex" marginBottom={16} alignItems="flex-start">
                                <Pane width={240} paddingRight={24}/>
                                <Pane flex={1}>
                                    <InlineWarningPanel>{warningContent}</InlineWarningPanel>
                                </Pane>
                            </Pane>
                        )}
                    </div>
                </div>

                <div className="w-full flex border-t dark:border-gray-600 p-6 justify-end">
                    <div className="flex w-1/3 justify-end items-center">
                        <FatOutlineButton
                            color="red"
                            text="Cancel"
                            onClick={onClose}
                            type="button"
                            disabled={loading}
                        />
                        <span style={{width: 12}}/>
                        <FatOutlineButton
                            color="green"
                            text="Submit"
                            type="submit"
                            disabled={isBlocked || loading}
                        />
                    </div>
                </div>
            </form>
            </FormProvider>
        </ReportModal>
    );
}
